import { Component, Input, Output, EventEmitter, Signal, computed } from '@angular/core';
import { BaseComponent } from '../common/base.component';
import { OAuthService } from '../services/o-auth.service';
import { PoeApiService, PoeFilter, FilterUpdateDelayedError } from '../services/poe-api.service';
import { FilterService } from '../services/filter.service';
import { FilterImporterService } from '../services/filter-importer.service';
import { FilterImportResults } from '../models/filter-import.models';
import { LOCAL_STORAGE_KEY_FILTER_DEFAULT_REALM, SYNC_WARNING_FOR_NEW_VERSION, DISABLE_SYNC_UNTIL_REALM_DOWN, LOCAL_STORAGE_KEY_FORCE_ALLOW_SYNC, MAX_FILTER_NAME_LENGTH } from '../const';
import { StoredFilter } from '../models/filter-models';
import { ACCOUNT_FILTER_DEFAULT_REALM, abbrevGameName } from '../../game';
import { logEvent } from '../app.config';
import { TooltipWrapperComponent } from '../common/tooltip-wrapper/tooltip-wrapper.component';
import { FileSaveService } from '../services/file-save.service';
import { FilterSnapshotService } from '../services/filter-snapshot.service';
import { DelayedMouseleaveDirective } from '../common/delayed-mouseleave/delayed-mouseleave.directive';
import { ToastService } from '../services/toast.service';

@Component({
  selector: 'app-controls',
  templateUrl: './controls.component.html',
  styleUrl: './controls.component.scss',
  standalone: true,
  imports: [TooltipWrapperComponent, DelayedMouseleaveDirective]
})
export class ControlsComponent extends BaseComponent {
  @Input() showLeft: boolean = true;
  @Input() showCenter: boolean = true;
  @Input() showRight: boolean = true;
  @Input() isBeta: boolean = false;
  @Input() isLegacy: boolean = false;
  @Input() tab: string = '';
  @Input() initialVisit: boolean = false;
  @Input() floating: boolean = false;
  @Output() presetsClick = new EventEmitter<void>();
  @Output() filterImport = new EventEmitter<FilterImportResults>();
  @Output() sharedFilterImport = new EventEmitter<{
    importResult: FilterImportResults;
    filterId: string;
    username?: string | null;
  }>();

  storedFilter!: Signal<any>;
  filterResults!: Signal<any>;
  userName: Signal<string | null>;

  saveButtonText: ReturnType<typeof computed<string>>;
  showGameFolderHelp: ReturnType<typeof computed<boolean>>;
  supportsFileSystemAccess: boolean;

  synchronizing = false;
  syncText = `Sync to ${abbrevGameName}`;
  copyText = 'Copy to Clipboard';

  syncTimestamps: number[] = [];
  syncDisabled = false;
  syncWarningUntilRealmDown = SYNC_WARNING_FOR_NEW_VERSION;
  disableSyncUntilRealmDown = DISABLE_SYNC_UNTIL_REALM_DOWN && !localStorage.getItem(LOCAL_STORAGE_KEY_FORCE_ALLOW_SYNC);
  syncCountdown = '';
  syncWarning = '';

  showImportOptions = false;

  constructor(
    private loginService: OAuthService,
    private poeApiService: PoeApiService,
    private filterService: FilterService,
    private filterImporterService: FilterImporterService,
    private fileSaveService: FileSaveService,
    private filterSnapshotService: FilterSnapshotService,
    private toastService: ToastService,
  ) {
    super();
    this.userName = this.loginService.userName;
    this.storedFilter = this.filterService.activeFilter;
    this.filterResults = this.filterService.filterResultData;

    this.supportsFileSystemAccess = this.fileSaveService.supportsFileSystemAccess;

    this.saveButtonText = computed(() => {
      if (!this.fileSaveService.supportsFileSystemAccess) return 'Download';
      return this.fileSaveService.hasGameDirectory() ? 'Save to file' : 'Select game folder';
    });

    this.showGameFolderHelp = computed(() =>
      this.fileSaveService.supportsFileSystemAccess && !this.fileSaveService.hasGameDirectory()
    );
  }

  syncOrLogin() {
    if (this.userName()) {
      this.synchronizing = true;
      const realm = localStorage.getItem(LOCAL_STORAGE_KEY_FILTER_DEFAULT_REALM) || ACCOUNT_FILTER_DEFAULT_REALM;
      const filterType = this.storedFilter().filter.ruthless ? 'Ruthless' : undefined;
      if (!this.storedFilter().id) {
        logEvent('filter', 'sync', 'creating_on_' + realm);
        this.syncText = 'Creating...';
        this.poeApiService.createFilter(this.storedFilter().name, this.filterResults().filterTextFull, realm, filterType)
          .then((poeFilter: PoeFilter) => {
            this.storedFilter().id = poeFilter.id;
            this.filterService.updateFilter();
            this.handleSyncSuccess();
          })
          .catch(error => this.handleSyncError(error))
          .finally(() => this.synchronizing = false);
      } else {
        logEvent('filter', 'sync', 'updating');
        this.syncText = 'Synchronizing...';
        this.poeApiService.updateFilter(this.storedFilter().id!, this.storedFilter().name, this.filterResults().filterTextFull, realm, filterType)
          .then(() => this.handleSyncSuccess())
          .catch(error => this.handleSyncError(error))
          .finally(() => this.synchronizing = false);
      }

      const now = Date.now();
      this.syncTimestamps.push(now);
      this.syncTimestamps = this.syncTimestamps.filter(timestamp => now - timestamp < 600000); // Keep only timestamps within the last 10 minutes

      if (this.syncTimestamps.length >= 2) {
        const waitTime = 600000 - (now - this.syncTimestamps[0]);
        this.syncDisabled = true;
        this.startCountdown(waitTime);
        return;
      }
    } else {
      this.loginService.login();
    }
  }

  private handleSyncSuccess() {
    this.syncWarning = '';
    this.syncText = 'Synchronized!';
    this.filterSnapshotService.uploadSnapshot(this.storedFilter(), 'sync');
    setTimeout(() => this.syncText = `Sync to ${abbrevGameName}`, 1000);
  }

  private handleSyncError(error: any) {
    if (error instanceof FilterUpdateDelayedError) {
      this.syncText = 'Sync Delayed';
      setTimeout(() => this.syncText = `Sync to ${abbrevGameName}`, 1000);
      this.syncWarning = error.message;
    } else {
      const errorMessage = ('Error: ' + (error?.toString() || 'Unspecified')).substring(0, 100);
      console.error(errorMessage);
      logEvent('filter', 'failed_to_sync', errorMessage);
      this.syncText = 'Failed to Sync';
      this.syncWarning = errorMessage;
    }
  }

  private startCountdown(waitTime: number) {
    const interval = setInterval(() => {
      const minutes = Math.floor(waitTime / 60000);
      if (minutes > 0) {
        this.syncCountdown = `Please wait ${minutes + 1} min`;
      } else {
        this.syncCountdown = `Please wait ${Math.floor(waitTime / 1000)} sec`;
      }

      if (waitTime <= 0) {
        clearInterval(interval);
        this.syncDisabled = false;
        this.syncCountdown = '';
      }

      waitTime -= 1000;
    }, 1000);
  }

  save() {
    const stored = this.storedFilter() as StoredFilter;
    const extension = stored.filter.ruthless ? '.ruthlessfilter' : '.filter';
    const filename = stored.name + extension;
    const text = this.filterResults().filterTextFull;

    if (this.fileSaveService.supportsFileSystemAccess) {
      if (this.fileSaveService.hasGameDirectory()) {
        logEvent('filter', 'save_to_file');
        void this.fileSaveService.save(text, filename).then(() => {
          this.filterSnapshotService.uploadSnapshot(stored, 'save');
        });
      } else {
        logEvent('filter', 'select_game_folder');
        void this.fileSaveService.selectGameDirectory();
      }
    } else {
      logEvent('filter', 'download');
      this.downloadTextAsFile(text, filename);
      this.filterSnapshotService.uploadSnapshot(stored, 'download');
    }
  }

  private downloadTextAsFile(text: string, filename: string) {
    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text));
    element.setAttribute('download', filename);

    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }

  copyToClipboard() {
    logEvent('filter', 'copy');
    navigator.clipboard.writeText(this.filterResults().filterTextFull);
    this.filterSnapshotService.uploadSnapshot(this.storedFilter() as StoredFilter, 'copy');
    this.copyText = 'Copied!';
    setTimeout(() => this.copyText = 'Copy to Clipboard', 1000);
  }

  clear() {
    if (confirm("Are you sure you want to reset your filter to the defaults? This cannot be undone.")) {
      this.filterService.resetFilter();
    }
  }

  importFilter() {
    logEvent('import', 'import');
    this.import((importResult: FilterImportResults) => {
      this.filterImport.emit(importResult);
    });
  }

  private import(callback: (importResult: FilterImportResults) => void) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.filter,.ruthlessfilter';
    input.style.display = 'none';
    input.onchange = (event: Event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          let result = new FilterImportResults();
          try {
            const content = e.target!.result as string;
            result = this.filterImporterService.importFilter(content);
            if (result.importFilterSuccess) {
              result.importedName = this.sanitizeFilterFileName(file.name);
            }
          } catch (error: any) {
            result.importFilterError = error;
          } finally {
            document.body.removeChild(input);
            callback(result);
          }
        };
        reader.readAsText(file);
      } else {
        document.body.removeChild(input);
      }
    };
    document.body.appendChild(input);
    input.click();
  }

  private sanitizeFilterFileName(fileName: string): string {
    let name = fileName.replace(/\.ruthlessfilter$/i, '').replace(/\.filter$/i, '');
    name = name.replace(/[^a-zA-Z0-9 .-]/g, '').trim();
    if (!name) {
      name = 'Imported Filter';
    }
    return name.substring(0, MAX_FILTER_NAME_LENGTH);
  }

  async importFromClipboard() {
    try {
      const content = await navigator.clipboard.readText();
      const trimmed = content.trim();
      if (!trimmed) {
        this.toastService.raise('warning', 'Clipboard is empty.');
        return;
      }

      // Full filter export (rules + embedded config)
      if (this.filterImporterService.looksLikeFilterExport(trimmed)) {
        const importResult = this.filterImporterService.importFilter(trimmed);
        if (this.emitLocalImport(importResult)) return;

        const message = importResult.importFilterError?.startsWith('Error migrating')
          ? importResult.importFilterError
          : 'Clipboard looks like a filter export, but it could not be imported.';
        this.toastService.raise('warning', message);
        return;
      }

      const shareRef = this.filterSnapshotService.parseShareInput(trimmed);
      const looksLikeShareUrl = /\/f\/[0-9A-Za-z]+/i.test(trimmed);

      // Full share link (before JSON / bare id so URLs win clearly)
      if (shareRef && looksLikeShareUrl) {
        await this.emitSharedImport(shareRef);
        return;
      }

      // JSON config (StoredFilter or Filter)
      const jsonResult = this.filterImporterService.importFilterJson(trimmed);
      if (this.emitLocalImport(jsonResult)) return;
      if (jsonResult.importFilterError?.startsWith('Error migrating')) {
        this.toastService.raise('warning', jsonResult.importFilterError);
        return;
      }

      // Bare filter id
      if (shareRef) {
        await this.emitSharedImport(shareRef);
        return;
      }

      this.toastService.raise(
        'warning',
        'Clipboard does not contain a filter, config, share link, or filter id.'
      );
    } catch {
      this.toastService.raise('warning', 'Could not read the clipboard.');
    }
  }

  private emitLocalImport(importResult: FilterImportResults): boolean {
    if (!importResult.importFilterSuccess || !importResult.filter) {
      return false;
    }
    importResult.importedName = importResult.importedName || 'Imported Filter';
    this.filterImport.emit(importResult);
    this.showImportOptions = false;
    return true;
  }

  private async emitSharedImport(ref: { filterId: string; username?: string | null }) {
    const importResult = await this.filterSnapshotService.loadSharedFilter(ref);
    if (!importResult) return;

    this.sharedFilterImport.emit({
      importResult,
      filterId: ref.filterId,
      username: ref.username,
    });
    this.showImportOptions = false;
  }

  onPresetsClick() {
    this.presetsClick.emit();
  }
}

